const KEYWORD_ENTRY_TYPES = ['claim_phrase', 'brand_handle', 'programme', 'executive'];

const TOP_POSTS = 3;

const stripPossessive = s => s.replace(/['’]s$/i, '');

const HONORIFICS = new Set(['dr', 'prof', 'professor', 'mr', 'mrs', 'ms', 'miss', 'sir', 'dame']);

const escapeRe = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Lookarounds, not the word-boundary escape, so anchors starting or ending in a non-word char ("@handle") still match whole words.
function termRegex(term) {
    return new RegExp(`(?<![\\p{L}\\p{N}_])${escapeRe(term)}(?![\\p{L}\\p{N}_])`, 'iu');
}

export function topicDefsFromEntries(entries) {
    const defs = [];
    const seen = new Set();
    (entries || []).forEach(e => {
        if (!KEYWORD_ENTRY_TYPES.includes(e.entry_type) || e.is_active === false) return;
        const v = e.value || {};
        let name = '';
        let terms = [];
        if (e.entry_type === 'claim_phrase') {
            name = stripPossessive(String(v.phrase || '').trim());
            terms = [name];
        } else if (e.entry_type === 'brand_handle') {
            const h = String(v.canonical_handle || '').replace(/^@+/, '').trim();
            if (h) { name = `@${h}`; terms = [h]; }
        } else if (e.entry_type === 'programme') {
            name = String(v.name || '').trim();
            terms = [name, ...(v.aliases || [])];
        } else {
            name = String(v.name || '').trim();
            const parts = name.split(/\s+/).filter(p => !HONORIFICS.has(p.toLowerCase().replace(/\.$/, '')));
            terms = parts.length > 1 && parts[0].length >= 3 ? [name, parts[0]] : [name];
        }
        terms = [...new Set(terms.map(t => String(t || '').trim()).filter(Boolean))];
        const key = name.toLowerCase();
        if (!name || !terms.length || seen.has(key)) return;
        seen.add(key);
        defs.push({ name, entryType: e.entry_type, terms, regexes: terms.map(termRegex) });
    });
    return defs;
}

export function topicDefsFromMonitored(list) {
    return (list || []).filter(t => t && typeof t === 'object').map(t => {
        const name = String(t.name || '').trim();
        const terms = [...new Set((t.keywords || []).map(k => String(k || '').trim()).filter(Boolean))];
        const excludes = (t.exclude || []).map(x => String(x || '').trim()).filter(Boolean);
        return {
            name, terms, custom: true,
            description: t.description || (terms.length > 1
                ? `Monitored keywords: ${terms.slice(0, 4).join(', ')}${terms.length > 4 ? ` +${terms.length - 4} more` : ''}.`
                : 'Monitored keyword.'),
            regexes: terms.map(termRegex),
            excludeRegexes: excludes.map(termRegex),
        };
    }).filter(d => d.name && d.terms.length);
}

const matchesDef = (text, def) => def.regexes.some(re => re.test(text || ''))
    && !(def.excludeRegexes || []).some(re => re.test(text || ''));

const ENTRY_LABEL = {
    claim_phrase: 'claim phrase', brand_handle: 'brand handle',
    programme: 'programme', executive: 'executive',
};

export function buildKeywordTopics(current, prior, days, defs) {
    if (!defs.length) return [];

    const dayKey = d => d.toISOString().slice(0, 10);
    const today = new Date();
    const axis = Array.from({ length: days }, (_, i) => {
        const d = new Date(today);
        d.setUTCDate(d.getUTCDate() - (days - 1 - i));
        return dayKey(d);
    });
    const priorAxis = axis.map(k => {
        const d = new Date(`${k}T00:00:00Z`);
        d.setUTCDate(d.getUTCDate() - days);
        return dayKey(d);
    });
    const total = current.length;

    return defs.map(def => {
        const matches = current.filter(m => matchesDef(m.text, def));
        if (!matches.length && !def.custom) return null;
        const spark = Object.fromEntries(axis.map(d => [d, 0]));
        const sentiment = { positive: 0, neutral: 0, negative: 0 };
        const counts = { engagement: 0, likes: 0, comments: 0, shares: 0 };
        let last = null;
        matches.forEach(m => {
            const s = String(m.sentiment || 'neutral').toLowerCase();
            sentiment[s in sentiment ? s : 'neutral'] += 1;
            counts.engagement += Number(m.engagement) || 0;
            counts.likes += Number(m.like_count) || 0;
            counts.comments += Number(m.reply_count) || 0;
            counts.shares += Number(m.retweet_count) || 0;
            const when = mentionDate(m);
            if (when) {
                const k = dayKey(when);
                if (k in spark) spark[k] += 1;
                if (!last || when > last) last = when;
            }
        });
        const priorMatches = prior.filter(m => matchesDef(m.text, def));
        const priorCount = priorMatches.length;
        const priorSpark = Object.fromEntries(priorAxis.map(d => [d, 0]));
        priorMatches.forEach(m => {
            const when = mentionDate(m);
            const k = when && dayKey(when);
            if (k in priorSpark) priorSpark[k] += 1;
        });
        const byPlatform = {};
        matches.forEach(m => {
            const p = String(m.platform || 'other').toLowerCase();
            byPlatform[p] = (byPlatform[p] || 0) + 1;
        });
        return {
            keyword: def.name,
            title: def.name,
            description: def.description || `Scan keyword (${ENTRY_LABEL[def.entryType]}).`,
            keywords: def.terms,
            ...(def.custom ? { is_custom: true, is_user_defined: true } : {}),
            mentions: matches.length,
            ...counts,
            share_of_voice_pct: total ? Math.round((matches.length / total) * 10000) / 100 : 0,
            sentiment,
            sparkline: axis.map(d => ({ date: d, mentions: spark[d] })),
            prior_sparkline: priorAxis.map(d => ({ date: d, mentions: priorSpark[d] })),
            delta_pct: deltaPct(matches.length, priorCount),
            prior_mentions: priorCount,
            last_match_at: last ? last.toISOString() : null,
            platforms: Object.entries(byPlatform)
                .map(([platform, count]) => ({ platform, count }))
                .sort((a, b) => b.count - a.count),
            top_posts: [...matches]
                .sort((a, b) => (Number(b.engagement) || 0) - (Number(a.engagement) || 0)
                    || (mentionDate(b) || 0) - (mentionDate(a) || 0))
                .slice(0, TOP_POSTS),
            matches: [...matches].sort((a, b) => (mentionDate(b) || 0) - (mentionDate(a) || 0)),
        };
    }).filter(Boolean);
}

// The window filter runs on scan time, so bucket on it too; posted_at is the
// fallback for rows from before scanned_at was exposed.
export function mentionDate(m) {
    const raw = m.scanned_at || m.posted_at;
    const d = raw ? new Date(raw) : null;
    return d && !isNaN(d) ? d : null;
}

function deltaPct(cur, prior) {
    if (!prior) return null;
    return Math.round(((cur - prior) / prior) * 1000) / 10;
}
